import { useEffect, useRef, useState } from "react";
import {
  CHANNEL_LABELS,
  TEMPLATE_KEY_LABELS,
  templateVariablesFor,
  type MessageChannel,
  type MessageTemplate,
  type TemplatePreview,
} from "@shared/modules/x-messagerie";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Field, Input, Select } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { ApiError } from "../../../core/lib/api";
import { formatDateTime } from "../../../core/lib/format";
import { useToastStore } from "../../../core/stores/toast";
import { PreviewBlock } from "../components/bubble";
import { HighlightedTextarea, VariableChips, insertVariable } from "../components/variables";
import {
  atelierEnabled,
  listAppointmentsForPreview,
  listTemplates,
  previewTemplate,
  sendTemplateTest,
  updateTemplate,
  type AppointmentHit,
} from "../lib/api";

/** Destinataire des envois d'essai, gardé d'une visite à l'autre sur ce poste. */
const TEST_TO_KEY = "messagerie.modeles.testTo";
function readTestTo(): string {
  try {
    return localStorage.getItem(TEST_TO_KEY) ?? "";
  } catch {
    return "";
  }
}
function storeTestTo(value: string): void {
  try {
    localStorage.setItem(TEST_TO_KEY, value);
  } catch {
    // Stockage indisponible (navigation privée) : le numéro vaut pour la visite.
  }
}

interface Draft {
  channel: MessageChannel;
  subject: string;
  body: string;
  enabled: boolean;
}

function toDraft(t: MessageTemplate): Draft {
  return { channel: t.channel, subject: t.subject ?? "", body: t.body, enabled: t.enabled };
}

function isDirty(t: MessageTemplate, d: Draft): boolean {
  return (
    d.channel !== t.channel ||
    d.subject !== (t.subject ?? "") ||
    d.body !== t.body ||
    d.enabled !== t.enabled
  );
}

/** Édition d'un modèle : activé, canal, objet, corps, aperçu sur un rendez-vous choisi. */
function TemplateEditor({
  template,
  appointmentId,
  testTo,
  onSaved,
}: {
  template: MessageTemplate;
  appointmentId: string;
  /** Cellulaire ou courriel des envois d'essai (barre d'outils) ; vide = bouton inactif. */
  testTo: string;
  onSaved: (t: MessageTemplate) => void;
}) {
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<Draft>(() => toDraft(template));
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setDraft(toDraft(template));
  }, [template]);

  // L'aperçu se rapporte à un rendez-vous : changer de rendez-vous l'invalide.
  useEffect(() => {
    setPreview(null);
  }, [appointmentId]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const dirty = isDirty(template, draft);
  const variables = templateVariablesFor(template.key);

  // Une variable cliquée s'insère dans le dernier champ touché (corps par défaut).
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastField = useRef<"subject" | "body">("body");
  const insert = (name: string) => {
    const field = lastField.current;
    const el = field === "subject" ? subjectRef.current : bodyRef.current;
    set(field, insertVariable(el, draft[field], name));
  };

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const saved = await updateTemplate(template.id, {
        channel: draft.channel,
        subject: draft.subject.trim() || null,
        body: draft.body,
        enabled: draft.enabled,
      });
      onSaved(saved);
      push("success", "Modèle enregistré", saved.name);
    } catch {
      push("danger", "Enregistrement impossible", "Le corps ne peut pas être vide.");
    } finally {
      setSaving(false);
    }
  };

  const showPreview = async () => {
    setPreviewing(true);
    try {
      // L'aperçu rend le modèle enregistré : on enregistre d'abord ce qui a changé.
      if (dirty) await save();
      setPreview(await previewTemplate(template.id, appointmentId || undefined));
    } catch {
      push("danger", "Aperçu impossible", "Le rendez-vous choisi n'existe peut-être plus.");
    } finally {
      setPreviewing(false);
    }
  };

  // Envoi d'essai au destinataire de la barre d'outils : le modèle enregistré, rendu
  // comme l'aperçu, part tout de suite et se retrouve dans la boîte d'envoi.
  const sendTest = async () => {
    const to = testTo.trim();
    if (!to || testing || saving) return;
    setTesting(true);
    try {
      if (dirty) await save();
      const m = await sendTemplateTest(template.id, to, appointmentId || undefined);
      if (m.status === "envoye") push("success", "Test envoyé", `À ${m.toAddress}.`);
      else if (m.status === "simule")
        push("warning", "Test simulé", "Aucune clé d'envoi sur ce serveur : rien n'est parti.");
      else push("danger", "Envoi en échec", m.error ?? undefined);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      push(
        "danger",
        "Envoi impossible",
        code === "invalid_recipient"
          ? "Entrez un numéro de cellulaire ou un courriel valide dans « Tests à »."
          : "Vérifiez le destinataire et réessayez.",
      );
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card
      title={template.name}
      subtitle={`${TEMPLATE_KEY_LABELS[template.key] ?? "Modèle Urable"} · modifié ${formatDateTime(template.updatedAt, { dateStyle: "medium" })}`}
      actions={
        <label className="modele__toggle">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
          />
          {draft.enabled ? "Activé" : "Désactivé"}
        </label>
      }
      footer={
        <div className="modele__footer">
          <Button variant="ghost" onClick={showPreview} disabled={previewing || saving}>
            {previewing ? "Aperçu…" : "Aperçu"}
          </Button>
          <Button variant="primary" onClick={save} disabled={!dirty || saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      }
    >
      <div className={`modele${draft.enabled ? "" : " modele--off"}`}>
        <div className="form-grid">
          <Field
            label="Canal"
            hint="Sans l'adresse voulue, le client reçoit le message par l'autre canal."
          >
            <Select
              value={draft.channel}
              onChange={(e) => set("channel", e.target.value as MessageChannel)}
            >
              {Object.entries(CHANNEL_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Objet" hint="Courriel seulement ; les variables y sont acceptées.">
            <Input
              ref={subjectRef}
              value={draft.subject}
              onChange={(e) => set("subject", e.target.value)}
              onFocus={() => (lastField.current = "subject")}
              placeholder={template.name}
            />
          </Field>
        </div>
        <Field label="Corps du message" hint="Les variables surlignées sont remplacées à l’envoi.">
          <HighlightedTextarea
            ref={bodyRef}
            rows={template.key === "recap_journee" ? 9 : 5}
            value={draft.body}
            onChange={(e) => set("body", e.target.value)}
            onFocus={() => (lastField.current = "body")}
          />
        </Field>

        <VariableChips variables={variables} onInsert={insert} />

        {preview ? (
          <PreviewBlock
            preview={preview}
            fallbackTo="Aucune adresse disponible pour ce client"
            actions={
              testTo.trim() ? (
                <Button
                  variant="ghost"
                  onClick={() => void sendTest()}
                  disabled={testing || saving}
                >
                  {testing ? "Envoi…" : `Envoyer ce test à ${testTo.trim()}`}
                </Button>
              ) : (
                <span className="messages__muted">
                  Entrez un cellulaire dans « Tests à », en haut, pour recevoir ce message.
                </span>
              )
            }
          />
        ) : null}
      </div>
    </Card>
  );
}

/** Liste des modèles avec le rendez-vous d'aperçu partagé (ou l'exemple). */
export function ModelesPage() {
  const push = useToastStore((s) => s.push);
  const [templates, setTemplates] = useState<MessageTemplate[] | null>(null);
  const [appointments, setAppointments] = useState<AppointmentHit[]>([]);
  const [appointmentId, setAppointmentId] = useState("");
  const [testTo, setTestTo] = useState(readTestTo);

  useEffect(() => {
    storeTestTo(testTo);
  }, [testTo]);

  useEffect(() => {
    listTemplates()
      .then(setTemplates)
      .catch(() => push("danger", "Impossible de charger les modèles"));
    listAppointmentsForPreview()
      .then(setAppointments)
      .catch(() => setAppointments([]));
  }, [push]);

  const disabledCount = templates?.filter((t) => !t.enabled).length ?? 0;

  return (
    <div className="page modeles">
      <PageHeader
        title="Modèles de messages"
        subtitle="Textes envoyés automatiquement aux clients. Les variables entre doubles accolades sont remplacées à l'envoi."
        actions={
          disabledCount > 0 ? (
            <Badge variant="warning">
              {disabledCount} désactivé{disabledCount > 1 ? "s" : ""}
            </Badge>
          ) : null
        }
      />

      <div className="messagerie-toolbar">
        {atelierEnabled ? (
          <>
            <label className="messagerie-toolbar__label" htmlFor="modeles-apercu">
              Aperçu sur
            </label>
            <Select
              id="modeles-apercu"
              className="messagerie-toolbar__select"
              value={appointmentId}
              onChange={(e) => setAppointmentId(e.target.value)}
            >
              <option value="">Un rendez-vous fictif (exemple)</option>
              {appointments.map((a) => (
                <option key={a.id} value={a.id}>
                  {formatDateTime(a.startAt, { dateStyle: "medium", timeStyle: "short" })} —{" "}
                  {a.title} — {a.clientName}
                </option>
              ))}
            </Select>
          </>
        ) : null}
        <label className="messagerie-toolbar__label" htmlFor="modeles-test-to">
          Tests à
        </label>
        <Input
          id="modeles-test-to"
          className="messagerie-toolbar__input"
          type="text"
          inputMode="tel"
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="Cellulaire ou courriel"
          title="Destinataire des envois d'essai de chaque modèle (« Envoyer un test »)"
        />
      </div>

      {templates === null ? (
        <p className="messages__muted">Chargement…</p>
      ) : (
        <div className="modeles__list">
          {templates.map((t) => (
            <TemplateEditor
              key={t.id}
              template={t}
              appointmentId={appointmentId}
              testTo={testTo}
              onSaved={(saved) =>
                setTemplates((list) => list?.map((x) => (x.id === saved.id ? saved : x)) ?? list)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
